import { Flex, Box, Image, Text, Spinner } from "@chakra-ui/react";
import { Artist, ServerResponse } from "../../../types";
import { t } from "i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../../services/axiosInstance";
import { PaymentValueProps } from "./PaymentValueProps";
import { useEffect, useState } from "react";
import { TextField } from "../../fields/TextField";
import { DonationRecurrentCheckbox } from "./DonationRecurrentCheckbox";
import { FunnelComponentProps } from "../../funnel/types";
import useValidatedAmount from "../../fields/hooks/useValidatedAmount";
import {
  useIsSmallBreakpoint,
  useMobileBreakpoint,
} from "../../../utils/useBreakpoints";
import { DonationGoalProgressBar } from "./DonationGoalProgressBar";
import { PurchaseButton } from "./PurchaseButton";
import { useIsShowingEmbeddedStripeForm } from "../hooks/useIsShowingEmbeddedStripeForm";
import {
  createArtistDonationFromConfirmationToken,
  createArtistDonationFromSavedPaymentMethod,
  createRecurringArtistDonationFromConfirmationToken,
  createRecurringArtistDonationFromSavedPaymentMethod,
} from "../api/paymentApi";
import { EmbeddedStripePaymentForm } from "./EmbeddedStripePaymentForm";
import { usePaymentCompletion } from "../hooks/usePaymentCompletion";
import { FUNNEL_TYPES } from "../../funnel/utils/contants";

export function DonationModalBody({
  artistId,
  setFunnelType,
}: FunnelComponentProps) {
  const minDonation = 3;
  const isSmallBreakpoint = useIsSmallBreakpoint();
  const isMobile = useMobileBreakpoint();
  const queryClient = useQueryClient();
  const showEmbeddedForm = useIsShowingEmbeddedStripeForm();
  const artistQueryKey = ["artist", artistId];

  // We need to query artist in order to get donation data
  const { isLoading, data, error } = useQuery({
    queryKey: artistQueryKey,
    enabled: !!artistId,
    queryFn: async () =>
      apiClient.get<ServerResponse<Artist>>(`/v1/artists/${artistId}`),
    refetchOnWindowFocus: false,
  });

  const initialValue = 10;

  const [amountToDonate, setAmountToDonate] = useState<number | undefined>(
    initialValue,
  );
  const [amountValidationError, setAmountValidationError] = useState<string>();
  const [isRecurrentDonation, setIsRecurrentDonation] = useState(false);
  const { value, setValue } = useValidatedAmount(
    setAmountToDonate,
    initialValue.toString(),
  );

  const isAboveMinimum = () => {
    if (
      !amountToDonate ||
      (typeof amountToDonate === "number" && amountToDonate < minDonation)
    ) {
      setAmountValidationError(t("components.payments.donate.minimumValue"));
    } else {
      setAmountValidationError(undefined);
    }
  };

  useEffect(() => {
    if (amountToDonate) {
      const timeout = setTimeout(() => {
        isAboveMinimum();
      }, 200);
      return () => clearTimeout(timeout);
    } else {
      isAboveMinimum();
    }
  }, [amountToDonate]);

  const refreshDonationState = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: artistQueryKey }),
      queryClient.invalidateQueries({ queryKey: ["account-me"] }),
    ]);
  };

  // The same Stripe Elements form serves both one-time and recurring donations:
  // route the resulting ConfirmationToken to whichever endpoint matches the
  // recurring toggle at submit time, so toggling the checkbox doesn't remount
  // the form.
  //
  // Must run before the loading/error early returns below — otherwise the hook
  // count changes between the loading render and the data-ready render and
  // React's hook-order check crashes the component.
  const {
    runAndSwallow: runDonationAndSwallow,
    isLoading: checkoutWaiting,
    error: checkoutError,
  } = usePaymentCompletion<unknown>({
    updateCache: refreshDonationState,
    onSuccess: () => setFunnelType(FUNNEL_TYPES.donateConfirmation),
    fallbackMessage: t("components.payments.donate.donationDeclined"),
  });

  if (isLoading) {
    return (
      <Flex width="100%" justifyContent="center" padding="24px">
        <Spinner color="blaze.blaze" />
      </Flex>
    );
  }

  if (error || !data) {
    return null;
  }

  const {
    avatar_small_url,
    roman_name,
    monthly_donation_amount,
    monthly_donation_goal,
  } = data?.data.payload.results;

  const parsedDonationGoal = parseFloat(monthly_donation_goal);

  const netAmount = amountToDonate ?? initialValue;

  const handleConfirmationTokenComplete = (confirmationTokenId: string) => {
    runDonationAndSwallow(async () => {
      const create = isRecurrentDonation
        ? createRecurringArtistDonationFromConfirmationToken
        : createArtistDonationFromConfirmationToken;
      return create(artistId ?? 0, {
        netAmount,
        isRecurring: isRecurrentDonation,
        confirmationTokenId,
      });
    });
  };

  const handleSavedPaymentMethodClick = () => {
    runDonationAndSwallow(async () => {
      const create = isRecurrentDonation
        ? createRecurringArtistDonationFromSavedPaymentMethod
        : createArtistDonationFromSavedPaymentMethod;
      return create(artistId ?? 0, {
        netAmount,
        isRecurring: isRecurrentDonation,
      });
    });
  };

  const isSubmitDisabled = !!(
    amountValidationError ||
    !amountToDonate ||
    checkoutWaiting
  );

  const renderPaymentControl = () => {
    if (showEmbeddedForm) {
      return (
        <EmbeddedStripePaymentForm
          submitButtonText={t("components.payments.donate.donate")}
          disabled={isSubmitDisabled}
          onComplete={handleConfirmationTokenComplete}
        />
      );
    }

    return (
      <PurchaseButton
        text={t("components.payments.donate.donate")}
        onClick={handleSavedPaymentMethodClick}
        loading={checkoutWaiting}
        isDisabled={isSubmitDisabled}
      />
    );
  };

  return (
    <Flex
      flexDirection="column"
      gap="24px"
      width="100%"
      padding="24px"
    >
      <Flex alignSelf="start" flexDirection="row" width="100%">
        <Image
          borderRadius="24px"
          boxSize={isMobile ? "90px" : "139px"}
          src={avatar_small_url}
          alt="Creator Avatar"
          mr={6}
        />
        <Flex
          flexDirection="column"
          justifyContent={isMobile ? "center" : "space-between"}
          width="100%"
        >
          <Box>
            <Text variant="modalBody">
              {t("components.payments.donate.creator")}
            </Text>
            <Text variant="modalTitle">{roman_name}</Text>
          </Box>

          {/* Only show donation goal slider if the goal is greater than 0  */}
          {parsedDonationGoal > 0 && !isSmallBreakpoint && (
            <DonationGoalProgressBar
              donationGoal={monthly_donation_goal}
              amountDonated={monthly_donation_amount}
            />
          )}
        </Flex>
        {/* Only show donation goal slider if the goal is greater than 0  */}
      </Flex>
      {parsedDonationGoal > 0 && isSmallBreakpoint && (
        <DonationGoalProgressBar
          donationGoal={monthly_donation_goal}
          amountDonated={monthly_donation_amount}
        />
      )}
      <Box width="100%" height="1px" background="transparent.white.10" />
      <PaymentValueProps
        valueProps={[
          t("components.payments.donate.helpFund"),
          t("components.payments.donate.supporterBadge"),
          t("components.payments.donate.earlyAccess"),
        ]}
        alignItems="start"
        showSeparators={false}
      />
      <Box width="100%" height="1px" background="transparent.white.10" />
      <DonationRecurrentCheckbox onChange={setIsRecurrentDonation} />
      <Box width="100%" height="1px" background="transparent.white.10" />
      <Box mb="-8px">
        <Text fontSize="14px" mb={4} color="dune.700">
          {t("components.payments.donate.amountToDonate", {
            minDonation: minDonation,
          })}
        </Text>

        <TextField
          type="text"
          readonly={checkoutWaiting}
          value={value}
          height="48px"
          placeholder=""
          error={amountValidationError}
          onChange={setValue}
          onBlur={isAboveMinimum}
          inputMode="decimal"
          variant="outline"
          prefix="$"
        />
      </Box>
      <Flex flexDirection="column" gap="8px" width="100%">
        {renderPaymentControl()}
        {checkoutError ? (
          <Text color="error.300" fontSize="14px" lineHeight="130%">
            {checkoutError}
          </Text>
        ) : null}
      </Flex>
    </Flex>
  );
}
